import { useCallback, useEffect, useRef, useState } from 'react';

export type CopyFn = (text: string) => Promise<boolean>;

/**
 * Copies text and reports `copied` for two seconds, long enough for a visible
 * "Copied" state and a screen-reader announcement.
 */
export function useClipboard(resetAfterMs = 2000): { copied: boolean; copy: CopyFn } {
    const [copied, setCopied] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(
        () => () => {
            clearTimeout(timer.current);
        },
        [],
    );

    const copy = useCallback<CopyFn>(
        async (text) => {
            try {
                await navigator.clipboard.writeText(text);
            } catch {
                setCopied(false);

                return false;
            }

            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                setCopied(false);
            }, resetAfterMs);

            return true;
        },
        [resetAfterMs],
    );

    return { copied, copy };
}
